import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/Add';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutline';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import {
  createAdminContentItem,
  deleteAdminContentItem,
  fetchAdminContentModule,
  reorderAdminContentItems,
  updateAdminContentItem,
} from '../../services/contentAdminApi';
import { getApiErrorMessage } from '../../services/apiUtils';
import type {
  AdminContentModuleResponse,
  ContentModuleKey,
  HomepageEditalItem,
  SecadDocumentItem,
} from '../../types/content';
import { fileToPayload } from '../../utils/fileUpload';
import {
  adminDialogPaperSx,
  adminHeroSx,
  adminMetricIconSx,
  adminSoftChipSx,
  adminSummaryCardSx,
  adminTableCardSx,
} from './adminVisuals';

type ResourceItem = HomepageEditalItem | SecadDocumentItem;

type AdminResourceManagerPageProps = {
  moduleKey: ContentModuleKey;
  title: string;
  description: string;
  createLabel: string;
  emptyMessage: string;
  supportsTag?: boolean;
  tagLabel?: string;
  tagHelperText?: string;
};

function formatDate(value?: string | null) {
  if (!value) return '--';
  return new Date(value).toLocaleString('pt-BR');
}

function getResourceTag(item: ResourceItem) {
  return 'tag' in item ? item.tag || '' : '';
}

export default function AdminResourceManagerPage({
  moduleKey,
  title,
  description,
  createLabel,
  emptyMessage,
  supportsTag = false,
  tagLabel = 'Tag',
  tagHelperText = 'Se preferir, deixe em branco para usar a indicação padrão.',
}: AdminResourceManagerPageProps) {
  const [items, setItems] = useState<ResourceItem[]>([]);
  const [moduleMeta, setModuleMeta] = useState<AdminContentModuleResponse<ResourceItem> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasOrderChanges, setHasOrderChanges] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ResourceItem | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(
    null
  );
  const [form, setForm] = useState({
    title: '',
    tag: '',
    href: '',
    isActive: true,
  });

  const loadItems = useCallback(
    async (options?: { clearFeedback?: boolean }) => {
      setIsLoading(true);
      if (options?.clearFeedback !== false) {
        setFeedback(null);
      }

      try {
        const response = await fetchAdminContentModule<ResourceItem>(moduleKey);
        setItems(response.items);
        setModuleMeta(response);
        setHasOrderChanges(false);
      } catch (error) {
        setFeedback({
          type: 'error',
          message: getApiErrorMessage(error, 'Não foi possível carregar os itens deste módulo.'),
        });
      } finally {
        setIsLoading(false);
      }
    },
    [moduleKey]
  );

  React.useEffect(() => {
    void loadItems();
  }, [loadItems]);

  const summary = useMemo(() => {
    return {
      total: items.length,
      active: items.filter((item) => item.isActive).length,
      links: items.filter((item) => item.resourceType === 'link').length,
      files: items.filter((item) => item.resourceType !== 'link').length,
    };
  }, [items]);

  const resetDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setSelectedFile(null);
    setForm({
      title: '',
      tag: '',
      href: '',
      isActive: true,
    });
  };

  const openCreate = () => {
    setEditingItem(null);
    setSelectedFile(null);
    setForm({
      title: '',
      tag: supportsTag ? 'Documento oficial' : '',
      href: '',
      isActive: true,
    });
    setDialogOpen(true);
  };

  const openEdit = (item: ResourceItem) => {
    setEditingItem(item);
    setSelectedFile(null);
    setForm({
      title: item.title,
      tag: getResourceTag(item),
      href: item.href,
      isActive: item.isActive,
    });
    setDialogOpen(true);
  };

  const move = (index: number, direction: -1 | 1) => {
    setItems((current) => {
      const next = [...current];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= next.length) return current;
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      setHasOrderChanges(true);
      return next.map((item, orderIndex) => ({ ...item, order: orderIndex + 1 }));
    });
  };

  const persistOrder = async () => {
    setIsSubmitting(true);
    setFeedback(null);

    try {
      await reorderAdminContentItems<ResourceItem>(
        moduleKey,
        items.map((item) => item.id)
      );
      await loadItems({ clearFeedback: false });
      setFeedback({ type: 'success', message: 'Ordem de exibição atualizada com sucesso.' });
    } catch (error) {
      setFeedback({
        type: 'error',
        message: getApiErrorMessage(error, 'Não foi possível salvar a ordem de exibição.'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    setFeedback(null);

    try {
      const payload: Record<string, unknown> = {
        title: form.title,
        href: form.href,
        isActive: form.isActive,
      };

      if (supportsTag) {
        payload.tag = form.tag;
      }

      if (selectedFile) {
        payload.file = await fileToPayload(selectedFile);
      }

      if (editingItem) {
        await updateAdminContentItem<ResourceItem>(moduleKey, editingItem.id, payload);
        setFeedback({ type: 'success', message: 'Item atualizado com sucesso.' });
      } else {
        await createAdminContentItem<ResourceItem>(moduleKey, payload);
        setFeedback({ type: 'success', message: 'Item criado com sucesso.' });
      }

      resetDialog();
      await loadItems({ clearFeedback: false });
    } catch (error) {
      setFeedback({
        type: 'error',
        message: getApiErrorMessage(error, 'Não foi possível salvar este item.'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (item: ResourceItem) => {
    if (!window.confirm(`Remover "${item.title}"?`)) return;

    setIsSubmitting(true);
    setFeedback(null);

    try {
      await deleteAdminContentItem(moduleKey, item.id);
      await loadItems({ clearFeedback: false });
      setFeedback({ type: 'success', message: 'Item removido com sucesso.' });
    } catch (error) {
      setFeedback({
        type: 'error',
        message: getApiErrorMessage(error, 'Não foi possível remover o item selecionado.'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleActive = async (item: ResourceItem) => {
    setIsSubmitting(true);
    setFeedback(null);

    try {
      const response = await updateAdminContentItem<ResourceItem>(moduleKey, item.id, {
        title: item.title,
        href: item.href,
        tag: getResourceTag(item),
        isActive: !item.isActive,
      });

      await loadItems({ clearFeedback: false });
      setFeedback({
        type: 'success',
        message: response.item.isActive ? 'Item ativado com sucesso.' : 'Item desativado com sucesso.',
      });
    } catch (error) {
      setFeedback({
        type: 'error',
        message: getApiErrorMessage(error, 'Não foi possível atualizar o status deste item.'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSaveDisabled = isSubmitting || !form.title.trim() || (!form.href.trim() && !selectedFile);

  return (
    <Stack spacing={3}>
      <Box sx={adminHeroSx}>
        <Stack spacing={1.5} sx={{ position: 'relative', zIndex: 1 }}>
          <Chip label="Conteúdo institucional" variant="outlined" sx={adminSoftChipSx} />
          <Typography variant="h4" fontWeight={900} sx={{ color: '#16324f' }}>
            {title}
          </Typography>
          <Typography sx={{ maxWidth: 860, color: 'rgba(22, 50, 79, 0.88)' }}>{description}</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} useFlexGap flexWrap="wrap">
            <Chip
              label={hasOrderChanges ? 'Ordem alterada localmente' : 'Ordem sincronizada'}
              color={hasOrderChanges ? 'warning' : 'success'}
              variant="outlined"
              sx={{ bgcolor: 'rgba(255,255,255,0.76)', fontWeight: 700 }}
            />
            <Chip
              label={`Última atualização: ${formatDate(moduleMeta?.updatedAt)}`}
              variant="outlined"
              sx={{ bgcolor: 'rgba(255,255,255,0.76)' }}
            />
            <Chip
              label={`Última publicação: ${formatDate(moduleMeta?.lastPublishedAt || moduleMeta?.publishedAt)}`}
              variant="outlined"
              sx={{ bgcolor: 'rgba(255,255,255,0.76)' }}
            />
          </Stack>
        </Stack>
      </Box>

      {feedback && <Alert severity={feedback.type}>{feedback.message}</Alert>}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Card sx={adminSummaryCardSx}>
          <CardContent>
            <Stack spacing={1.5}>
              <Box sx={adminMetricIconSx('rgba(37, 99, 235, 0.12)', '#1d4ed8')}>
                <DescriptionRoundedIcon />
              </Box>
              <Typography variant="h6" fontWeight={900} sx={{ color: '#16324f' }}>
                {summary.total}
              </Typography>
              <Typography color="text.secondary">Itens cadastrados</Typography>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={adminSummaryCardSx}>
          <CardContent>
            <Stack spacing={1.5}>
              <Box sx={adminMetricIconSx('rgba(20, 184, 166, 0.14)', '#0f766e')}>
                <DescriptionRoundedIcon />
              </Box>
              <Typography variant="h6" fontWeight={900} sx={{ color: '#16324f' }}>
                {summary.active}
              </Typography>
              <Typography color="text.secondary">Itens ativos</Typography>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={adminSummaryCardSx}>
          <CardContent>
            <Stack spacing={1.5}>
              <Box sx={adminMetricIconSx('rgba(245, 158, 11, 0.16)', '#b45309')}>
                <InsertDriveFileRoundedIcon />
              </Box>
              <Typography variant="h6" fontWeight={900} sx={{ color: '#16324f' }}>
                {summary.files}
              </Typography>
              <Typography color="text.secondary">Arquivos enviados</Typography>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={adminSummaryCardSx}>
          <CardContent>
            <Stack spacing={1.5}>
              <Box sx={adminMetricIconSx('rgba(14, 165, 233, 0.14)', '#0369a1')}>
                <LinkRoundedIcon />
              </Box>
              <Typography variant="h6" fontWeight={900} sx={{ color: '#16324f' }}>
                {summary.links}
              </Typography>
              <Typography color="text.secondary">Links informados</Typography>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Card sx={adminTableCardSx}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h5" fontWeight={900} sx={{ color: '#16324f' }}>
                Gerenciamento
              </Typography>
              <Typography color="text.secondary">
                Cadastre, edite, reorganize e mantenha este conteúdo atualizado no site.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1}>
              <Tooltip title="Atualizar">
                <span>
                  <IconButton onClick={() => void loadItems()} disabled={isLoading || isSubmitting}>
                    <RefreshRoundedIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<SaveRoundedIcon />}
                onClick={() => void persistOrder()}
                disabled={!hasOrderChanges || isSubmitting}
                sx={{ borderRadius: 999 }}
              >
                Salvar ordem
              </Button>
              <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate} sx={{ borderRadius: 999 }}>
                {createLabel}
              </Button>
            </Stack>
          </Stack>

          <Alert severity="info" sx={{ mb: 2 }}>
            Você pode informar um endereço ou enviar um arquivo em PDF. Os itens ativos ficam disponíveis no site.
          </Alert>

          {moduleMeta?.lastPublicationStatus && moduleMeta.lastPublicationStatus !== 'published' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {moduleMeta.lastPublicationError ||
                'Houve uma divergência na última publicação. Revise este módulo antes de seguir.'}
            </Alert>
          )}

          {isLoading ? (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={22} />
              <Typography color="text.secondary">Carregando itens...</Typography>
            </Stack>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Título</TableCell>
                    {supportsTag && <TableCell>Tag</TableCell>}
                    <TableCell>Destino</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Ordem</TableCell>
                    <TableCell>Atualizado em</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Typography fontWeight={800} sx={{ color: '#16324f' }}>
                          {item.title}
                        </Typography>
                      </TableCell>
                      {supportsTag && <TableCell>{getResourceTag(item) || '--'}</TableCell>}
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                            {item.href}
                          </Typography>
                          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Chip
                              label={item.resourceType === 'link' ? 'Link' : 'Arquivo'}
                              size="small"
                              variant="outlined"
                            />
                            <MuiLink
                              href={item.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              sx={{ fontSize: 13, fontWeight: 700, alignSelf: 'center' }}
                            >
                              Abrir em nova guia
                            </MuiLink>
                          </Stack>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.isActive ? 'Ativo' : 'Inativo'}
                          color={item.isActive ? 'success' : 'default'}
                          size="small"
                          variant={item.isActive ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>{item.order}</TableCell>
                      <TableCell>{formatDate(item.updatedAt)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <IconButton size="small" onClick={() => move(index, -1)} disabled={index === 0 || isSubmitting}>
                            <ArrowUpwardRoundedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => move(index, 1)}
                            disabled={index === items.length - 1 || isSubmitting}
                          >
                            <ArrowDownwardRoundedIcon fontSize="small" />
                          </IconButton>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => void handleToggleActive(item)}
                            disabled={isSubmitting}
                          >
                            {item.isActive ? 'Desativar' : 'Ativar'}
                          </Button>
                          <IconButton size="small" onClick={() => openEdit(item)} disabled={isSubmitting}>
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => void handleDelete(item)}
                            disabled={isSubmitting}
                          >
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!items.length && (
                    <TableRow>
                      <TableCell colSpan={supportsTag ? 7 : 6}>
                        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                          {emptyMessage}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={resetDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: adminDialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 900, color: '#16324f' }}>
          {editingItem ? 'Editar item' : createLabel}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Título"
              value={form.title}
              onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
              fullWidth
              required
            />

            {supportsTag && (
              <TextField
                label={tagLabel}
                value={form.tag}
                onChange={(event) => setForm((current) => ({ ...current, tag: event.target.value }))}
                fullWidth
                helperText={tagHelperText}
              />
            )}

            <TextField
              label="Link ou arquivo"
              value={form.href}
              onChange={(event) => setForm((current) => ({ ...current, href: event.target.value }))}
              fullWidth
              helperText="Se enviar um arquivo, ele será usado no lugar do endereço informado."
            />

            <Button component="label" variant="outlined" sx={{ borderRadius: 999 }}>
              {selectedFile ? `Arquivo selecionado: ${selectedFile.name}` : 'Escolher arquivo'}
              <input
                hidden
                type="file"
                accept="application/pdf"
                onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
              />
            </Button>

            <Button
              variant={form.isActive ? 'contained' : 'outlined'}
              onClick={() => setForm((current) => ({ ...current, isActive: !current.isActive }))}
              sx={{ borderRadius: 999 }}
            >
              {form.isActive ? 'Item ativo' : 'Item inativo'}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={resetDialog}>Cancelar</Button>
          <Button variant="contained" onClick={() => void handleSave()} disabled={isSaveDisabled} sx={{ borderRadius: 999 }}>
            {isSubmitting ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
